// src/modules/orders/components/OrderDetailModal.tsx
import React from 'react';
import { Modal, Button, Descriptions, Tag, Divider, Typography, Space } from 'antd';
import { PrinterOutlined, CarOutlined, GiftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { Order } from '@/services/api/orders';
import { 
  processOrderData, 
  getMealNames, 
  formatOrderItemLine,
  formatGiftItemLine,  // 🎁 НОВОЕ
  formatPrice 
} from '../utils/orderUtils';

const { Text } = Typography;

// Статусы заказов
const ORDER_STATUS_MAP = {
  pending: { color: 'orange', text: 'Ожидает' },
  confirmed: { color: 'blue', text: 'Подтвержден' },
  preparing: { color: 'yellow', text: 'Формируется' },
  delivering: { color: 'cyan', text: 'Доставляется' },
  completed: { color: 'green', text: 'Завершен' },
  cancelled: { color: 'red', text: 'Отменен' },
  abandoned: { color: 'gray', text: 'Брошенная корзина' },
};

// Расширенный интерфейс заказа с дополнительными полями
interface ExtendedOrder extends Order {
  order_data?: any;
  customer_comment?: string;
}

interface OrderDetail {
  order: ExtendedOrder;
  additional_info: {
    days_since_created: number;
    payment_expired: boolean;
    order_items_count: number;
    formatted_address: string;
  };
}

interface OrderDetailModalProps {
  visible: boolean;
  orderDetail: OrderDetail | null;
  onClose: () => void;
}

export const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  visible,
  orderDetail,
  onClose,
}) => {
  const handlePrint = () => {
    // Создаем новое окно для печати
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const groupedMeals = processOrderData(orderDetail?.order.order_data);
    const mealNames = getMealNames();

    // Формируем содержимое для печати
    let orderCompositionHTML = '';
    if (groupedMeals.totalItems > 0) {
      // Завтрак
      orderCompositionHTML += `<div style="margin-bottom: 15px;">`;
      orderCompositionHTML += `<strong>${mealNames.breakfast}:</strong><br>`;
      if (groupedMeals.breakfast.length === 0) {
        orderCompositionHTML += `<div style="margin-left: 15px; color: #666;">нет позиций</div>`;
      } else {
        groupedMeals.breakfast.forEach(item => {
          orderCompositionHTML += `<div style="margin-left: 15px;">${formatOrderItemLine(item)}</div>`;
        });
      }
      orderCompositionHTML += `</div>`;

      // Обед
      orderCompositionHTML += `<div style="margin-bottom: 15px;">`;
      orderCompositionHTML += `<strong>${mealNames.lunch}:</strong><br>`;
      if (groupedMeals.lunch.length === 0) {
        orderCompositionHTML += `<div style="margin-left: 15px; color: #666;">нет позиций</div>`;
      } else {
        groupedMeals.lunch.forEach(item => {
          orderCompositionHTML += `<div style="margin-left: 15px;">${formatOrderItemLine(item)}</div>`;
        });
      }
      orderCompositionHTML += `</div>`;

      // Ужин
      orderCompositionHTML += `<div style="margin-bottom: 15px;">`;
      orderCompositionHTML += `<strong>${mealNames.dinner}:</strong><br>`;
      if (groupedMeals.dinner.length === 0) {
        orderCompositionHTML += `<div style="margin-left: 15px; color: #666;">нет позиций</div>`;
      } else {
        groupedMeals.dinner.forEach(item => {
          orderCompositionHTML += `<div style="margin-left: 15px;">${formatOrderItemLine(item)}</div>`;
        });
      }
      orderCompositionHTML += `</div>`;

      // ============================================
      // 🎁 НОВОЕ: Раздел подарков в печати
      // ============================================
      if (groupedMeals.gifts && groupedMeals.gifts.length > 0) {
        orderCompositionHTML += `<div style="margin-bottom: 15px; background: #fff8e1; padding: 10px; border-radius: 4px; border: 1px dashed #ffc107;">`;
        orderCompositionHTML += `<strong>🎁 ${mealNames.gifts}:</strong><br>`;
        groupedMeals.gifts.forEach(gift => {
          orderCompositionHTML += `<div style="margin-left: 15px;">${formatGiftItemLine(gift)}</div>`;
        });
        orderCompositionHTML += `</div>`;
      }
      // ============================================

      // Итого
      orderCompositionHTML += `<hr style="margin: 20px 0;">`;
      orderCompositionHTML += `<div><strong>Итого: ${formatPrice(groupedMeals.totalPrice)}₸ (${groupedMeals.totalItems} позиций)</strong></div>`;
      
      // 🎁 НОВОЕ: Примечание о подарках
      if (groupedMeals.gifts && groupedMeals.gifts.length > 0) {
        const giftsCount = groupedMeals.gifts.reduce((sum, g) => sum + g.totalQuantity, 0);
        orderCompositionHTML += `<div style="margin-top: 8px; color: #666; font-size: 12px;">* ${giftsCount} подарок(ов) бесплатно</div>`;
      }
    } else {
      orderCompositionHTML = '<div>Состав заказа не найден</div>';
    }

    const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Детали заказа ${orderDetail?.order.order_number || ''}</title>
      <meta charset="utf-8">
      <style>
        body {
          font-family: Arial, sans-serif;
          font-size: 14px;
          line-height: 1.4;
          margin: 20px;
          color: black;
        }
        h1 {
          font-size: 18px;
          margin-bottom: 20px;
          border-bottom: 1px solid #ccc;
          padding-bottom: 10px;
        }
        h2 {
          font-size: 16px;
          margin: 20px 0 10px 0;
        }
        p {
          margin: 5px 0;
        }
        .customer-comment {
          background: #f5f5f5;
          padding: 10px;
          border-radius: 4px;
          margin: 10px 0;
        }
        @page {
          margin: 1cm;
          size: A4;
        }
      </style>
    </head>
    <body>
      <h1>Детали заказа ${orderDetail?.order.order_number || ''}</h1>
      
      <div style="margin-bottom: 20px;">
        <p><strong>Номер заказа:</strong> ${orderDetail?.order.order_number || ''}</p>
        <p><strong>Клиент:</strong> ${orderDetail?.order.customer_name || ''}</p>
        <p><strong>Телефон:</strong> ${orderDetail?.order.customer_phone || ''}</p>
        <p><strong>Телефон для оплаты:</strong> ${orderDetail?.order.payment_phone || 'Не указан'}</p>
        <p><strong>Адрес:</strong> ${orderDetail?.additional_info.formatted_address || ''}</p>
        <p><strong>Дата доставки:</strong> ${orderDetail?.order.delivery_date ? dayjs(orderDetail.order.delivery_date).format('DD.MM.YYYY') : ''}</p>
        <p><strong>Время доставки:</strong> ${orderDetail?.order.delivery_time_slot || 'Не указано'}</p>
        <p><strong>Создан:</strong> ${orderDetail?.order.created_at ? dayjs(orderDetail.order.created_at).format('DD.MM.YYYY HH:mm') : ''}</p>
        <p><strong>Стоимость доставки:</strong> ${
          orderDetail?.order.delivery_fee === 0 
            ? '<span style="color: #52c41a; font-weight: bold;">БЕСПЛАТНО</span>' 
            : `${formatPrice(orderDetail?.order.delivery_fee || 1500)}₸`
        }</p>
        <p><strong>Общая сумма с доставкой:</strong> ${formatPrice(orderDetail?.order.total_price || 0)}₸</p>
      </div>
  
      ${orderDetail?.order.customer_comment ? `
        <div style="margin-bottom: 20px;">
          <p><strong>Комментарий клиента:</strong></p>
          <div class="customer-comment">${orderDetail.order.customer_comment}</div>
        </div>
      ` : ''}
  
      <div>
        <h2>Состав заказа:</h2>
        ${orderCompositionHTML}
      </div>
    </body>
    </html>
  `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Ждем загрузки и печатаем
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };
  
  // 🚚 Печать для курьеров (упрощенный формат только с данными для доставки)
  const handlePrintForCourier = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const printContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Заказ ${orderDetail?.order.order_number || ''} - Доставка</title>
      <meta charset="utf-8">
      <style>
        body {
          font-family: Arial, sans-serif;
          font-size: 16px;
          line-height: 1.6;
          margin: 20px;
          color: black;
        }
        h1 {
          font-size: 24px;
          margin-bottom: 30px;
          border-bottom: 2px solid #333;
          padding-bottom: 10px;
        }
        .info-row {
          display: flex;
          margin-bottom: 15px;
          padding: 10px;
          border-bottom: 1px solid #e8e8e8;
        }
        .info-label {
          font-weight: bold;
          width: 200px;
          flex-shrink: 0;
        }
        .info-value {
          flex: 1;
        }
        .highlight {
          background: #fff3cd;
          padding: 15px;
          border-radius: 5px;
          margin: 20px 0;
          border: 2px solid #ffc107;
        }
        @page {
          margin: 1.5cm;
          size: A4;
        }
      </style>
    </head>
    <body>
      <h1>🚚 Информация для доставки</h1>
      
      <div class="info-row">
        <div class="info-label">Номер заказа:</div>
        <div class="info-value" style="font-size: 18px; font-weight: bold;">${orderDetail?.order.order_number || ''}</div>
      </div>

      <div class="info-row">
        <div class="info-label">Телефон:</div>
        <div class="info-value" style="font-size: 18px; font-weight: bold;">${orderDetail?.order.customer_phone || ''}</div>
      </div>

      <div class="highlight">
        <div style="font-weight: bold; margin-bottom: 5px;">Адрес доставки:</div>
        <div style="font-size: 18px;">${orderDetail?.additional_info.formatted_address || ''}</div>
      </div>

      <div class="info-row">
        <div class="info-label">Количество позиций:</div>
        <div class="info-value" style="font-size: 18px; font-weight: bold;">${orderDetail?.order.total_items || 0} шт.</div>
      </div>

      <div class="info-row">
        <div class="info-label">Дата доставки:</div>
        <div class="info-value" style="font-size: 18px; font-weight: bold;">${orderDetail?.order.delivery_date ? dayjs(orderDetail.order.delivery_date).format('DD.MM.YYYY') : ''}</div>
      </div>

      <div class="info-row">
        <div class="info-label">Время доставки:</div>
        <div class="info-value" style="font-size: 18px; font-weight: bold;">${orderDetail?.order.delivery_time_slot || 'Не указано'}</div>
      </div>

      ${orderDetail?.order.customer_comment ? `
        <div style="margin-top: 30px; padding: 15px; background: #f0f0f0; border-radius: 5px;">
          <div style="font-weight: bold; margin-bottom: 10px;">💬 Комментарий:</div>
          <div style="font-size: 16px;">${orderDetail.order.customer_comment}</div>
        </div>
      ` : ''}
    </body>
    </html>
  `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };

  const renderOrderComposition = () => {
    if (!orderDetail?.order.order_data) {
      return <Text type="secondary">Состав заказа не найден</Text>;
    }

    const groupedMeals = processOrderData(orderDetail.order.order_data);
    const mealNames = getMealNames();

    if (groupedMeals.totalItems === 0) {
      return <Text type="secondary">Заказ пустой</Text>;
    }

    return (
      <div className="order-composition">
        {/* Завтрак */}
        <div className="meal-section">
          <Text strong>{mealNames.breakfast}:</Text>
          {groupedMeals.breakfast.length === 0 ? (
            <div style={{ marginLeft: 16, marginTop: 4 }}>
              <Text type="secondary">нет позиций</Text>
            </div>
          ) : (
            <div style={{ marginLeft: 16, marginTop: 4 }}>
              {groupedMeals.breakfast.map((item, index) => (
                <div key={index} style={{ marginBottom: 4 }}>
                  <Text>{formatOrderItemLine(item)}</Text>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Обед */}
        <div className="meal-section" style={{ marginTop: 16 }}>
          <Text strong>{mealNames.lunch}:</Text>
          {groupedMeals.lunch.length === 0 ? (
            <div style={{ marginLeft: 16, marginTop: 4 }}>
              <Text type="secondary">нет позиций</Text>
            </div>
          ) : (
            <div style={{ marginLeft: 16, marginTop: 4 }}>
              {groupedMeals.lunch.map((item, index) => (
                <div key={index} style={{ marginBottom: 4 }}>
                  <Text>{formatOrderItemLine(item)}</Text>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Ужин */}
        <div className="meal-section" style={{ marginTop: 16 }}>
          <Text strong>{mealNames.dinner}:</Text>
          {groupedMeals.dinner.length === 0 ? (
            <div style={{ marginLeft: 16, marginTop: 4 }}>
              <Text type="secondary">нет позиций</Text>
            </div>
          ) : (
            <div style={{ marginLeft: 16, marginTop: 4 }}>
              {groupedMeals.dinner.map((item, index) => (
                <div key={index} style={{ marginBottom: 4 }}>
                  <Text>{formatOrderItemLine(item)}</Text>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ============================================ */}
        {/* 🎁 НОВОЕ: Раздел подарков */}
        {/* ============================================ */}
        {groupedMeals.gifts && groupedMeals.gifts.length > 0 && (
          <div 
            className="gift-section" 
            style={{ 
              marginTop: 16, 
              background: '#fff8e1', 
              padding: 12, 
              borderRadius: 6,
              border: '1px dashed #ffc107'
            }}
          >
            <Text strong>
              <GiftOutlined style={{ marginRight: 8, color: '#ffc107' }} />
              {mealNames.gifts}:
            </Text>
            <div style={{ marginLeft: 16, marginTop: 8 }}>
              {groupedMeals.gifts.map((gift, index) => (
                <div key={index} style={{ marginBottom: 4 }}>
                  <Text>{formatGiftItemLine(gift)}</Text>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* ============================================ */}

        {/* Итого */}
        <Divider />
        <div>
          <Text strong>
            Итого: {formatPrice(groupedMeals.totalPrice)}₸ ({groupedMeals.totalItems} позиций)
          </Text>
          {/* 🎁 НОВОЕ: Примечание о подарках */}
          {groupedMeals.gifts && groupedMeals.gifts.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                * {groupedMeals.gifts.reduce((sum, g) => sum + g.totalQuantity, 0)} подарок(ов) бесплатно
              </Text>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!orderDetail) {
    return null;
  }

  return (
    <Modal
      title={`Детали заказа ${orderDetail.order.order_number}`}
      open={visible}
      onCancel={onClose}
      width={800}
      footer={[
        <Button key="print" icon={<PrinterOutlined />} onClick={handlePrint}>
          Печать (клиент)
        </Button>,
        <Button key="print-courier" icon={<CarOutlined />} onClick={handlePrintForCourier} type="dashed">
          Печать (курьер)
        </Button>,
        <Button key="close" onClick={onClose}>
          Закрыть
        </Button>,
      ]}
    >
        <div className="modal-content">
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="Номер заказа">
              {orderDetail.order.order_number}
            </Descriptions.Item>
            <Descriptions.Item label="Статус">
              <Tag color={ORDER_STATUS_MAP[orderDetail.order.status]?.color}>
                {ORDER_STATUS_MAP[orderDetail.order.status]?.text}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Клиент">
              {orderDetail.order.customer_name}
            </Descriptions.Item>
            <Descriptions.Item label="Телефон">
              {orderDetail.order.customer_phone}
            </Descriptions.Item>
            <Descriptions.Item label="Телефон для оплаты">
              {orderDetail.order.payment_phone || 'Не указан'}
            </Descriptions.Item>
            <Descriptions.Item label="Адрес" span={2}>
              {orderDetail.additional_info.formatted_address}
            </Descriptions.Item>
            
            <Descriptions.Item label="Сумма заказа">
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {/* Полная сумма */}
                <div>
                  <span style={{ fontSize: '16px', fontWeight: 500 }}>
                    {formatPrice(orderDetail.order.total_price)} ₸
                  </span>
                </div>
                
                {/* Бонусы (если есть) */}
                {orderDetail.order.bonus_amount > 0 && (
                  <div style={{ 
                    padding: '8px', 
                    background: '#f6ffed', 
                    border: '1px solid #b7eb8f',
                    borderRadius: '4px'
                  }}>
                    <div style={{ color: '#52c41a', fontWeight: 'bold' }}>
                      Оплачено бонусами: -{formatPrice(orderDetail.order.bonus_amount)} ₸
                    </div>
                    <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '4px' }}>
                      {orderDetail.order.bonus_summary || 
                        (orderDetail.order.bonus_applied ? '✅ Списаны' : '⏳ Зарезервированы')
                      }
                    </div>
                    {orderDetail.order.bonus_payment_phone && (
                      <div style={{ fontSize: '11px', color: '#595959', marginTop: '2px' }}>
                        📞 {orderDetail.order.bonus_payment_phone}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Итого к оплате */}
                <div style={{ 
                  marginTop: '8px',
                  paddingTop: '8px',
                  borderTop: '2px solid #d9d9d9'
                }}>
                  <span style={{ fontSize: '13px', color: '#595959' }}>
                    💳 К оплате клиентом:
                  </span>{' '}
                  {orderDetail.order.amount_to_pay === 0 ? (
                    <span style={{ color: 'green', fontWeight: 'bold', fontSize: '15px' }}>
                      0 ₸ (💯 полностью оплачено)
                    </span>
                  ) : (
                    <span style={{ fontWeight: 'bold', fontSize: '16px', color: '#1890ff' }}>
                      {formatPrice(orderDetail.order.amount_to_pay)} ₸
                    </span>
                  )}
                </div>
              </Space>
            </Descriptions.Item>
          
          <Descriptions.Item label="Количество позиций">
            {orderDetail.order.total_items}
          </Descriptions.Item>
          <Descriptions.Item label="Дата доставки">
            {dayjs(orderDetail.order.delivery_date).format('DD.MM.YYYY')}
          </Descriptions.Item>
          <Descriptions.Item label="Время доставки">
            {orderDetail.order.delivery_time_slot || 'Не указано'}
          </Descriptions.Item>
          <Descriptions.Item label="Создан">
            {dayjs(orderDetail.order.created_at).format('DD.MM.YYYY HH:mm')}
          </Descriptions.Item>
          <Descriptions.Item label="Дней с создания">
            {orderDetail.additional_info.days_since_created}
          </Descriptions.Item>
        </Descriptions>

        {/* Комментарий клиента */}
        {orderDetail.order.customer_comment && (
          <>
            <Divider />
            <div>
              <Text strong>Комментарий клиента:</Text>
              <div style={{ marginTop: 8, padding: 12, background: '#f5f5f5', borderRadius: 6 }}>
                {orderDetail.order.customer_comment}
              </div>
            </div>
          </>
        )}

        {/* Состав заказа */}
        <Divider />
        <div>
          <Text strong>Состав заказа:</Text>
          <div style={{ marginTop: 12 }}>
            {renderOrderComposition()}
          </div>
        </div>
      </div>
    </Modal>
  );
};